import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  getAllGameTables,
  createGameTable,
  updateGameTable,
  deleteGameTable,
  getGameTableDetails,
  getCurrenciesList,
  getAllCashGameTables,
  getCashGameTableDetails,
  createCashGameTable,
  updateCashGameTable,
  deleteCashGameTable,
  getCashGameHands,
  getCashGameHandHistory,
  resetCashGameTable,
  initializeCashGameTables,
  getRakeConfigurationList,
  getBuyInConfigurationList,
  createBuyInConfiguration,
  createRakeConfiguration,
  deleteRakeConfiguration,
  cloneRakeConfiguration,
  getTablePerformanceAnalytics,
} from 'network/services/game-table.service'

const unwrapPayload = (res) => (res && typeof res === 'object' && 'data' in res ? res.data : res)

export const getAllGameTablesThunk = createAsyncThunk(
  'get/tables',
  async (data, thunkApi) => {
    try {
      const res = await getAllGameTables(data)
      return unwrapPayload(res) 
    } catch (error) {
      return thunkApi.rejectWithValue(error.message)
    }
  }
)

export const getGameTableDetailsThunk = createAsyncThunk(
  'get/table-details',
  async (data, thunkApi) => {
    try {
      const res = await getGameTableDetails(data)
      return unwrapPayload(res) 
    } catch (error) {
      return thunkApi.rejectWithValue(error.message)
    }
  }
)

export const createTable = createAsyncThunk(
  'create/table',
  async ({ data, onSuccess }, thunkApi) => {
    try {
      const res = await createGameTable(data)
      onSuccess()
      return res
    } catch (error) {
      return thunkApi.rejectWithValue(error.message)
    }
  }
)

export const updateTable = createAsyncThunk(
  'update/table',
  async ({ data, onSuccess, handleStatusDialogClose }, thunkApi) => {
    try {
      const res = await updateGameTable(data)
      if (onSuccess) onSuccess()
      if (handleStatusDialogClose) handleStatusDialogClose()
      return res
    } catch (error) {
      return thunkApi.rejectWithValue(error.message || 'Update failed')
    }
  }
)

export const deleteTable = createAsyncThunk(
  'delete/table',
  async ({ data, onSuccess }, thunkApi) => {
    try {
      const res = await deleteGameTable(data)
      onSuccess()
      return res
    } catch (error) {
      return thunkApi.rejectWithValue(error.message)
    }
  }
)

export const getAllCurrenciesList = createAsyncThunk(
  'get/currencies-list',
  async (data, thunkApi) => {
    try {
      const res = await getCurrenciesList(data)
      return res
    } catch (error) {
      return thunkApi.rejectWithValue(error.message)
    }
  }
)

export const getAllCashGameTablesThunk = createAsyncThunk(
  'get/cash-tables',
  async (data, thunkApi) => {
    try {
      const res = await getAllCashGameTables(data)
      return unwrapPayload(res)
    } catch (error) {
      return thunkApi.rejectWithValue(error.message)
    }
  }
)

export const getCashGameTableDetailsThunk = createAsyncThunk(
  'get/cash-table-details',
  async (data, thunkApi) => {
    try {
      const res = await getCashGameTableDetails(data)
      return unwrapPayload(res)
    } catch (error) {
      return thunkApi.rejectWithValue(error.message)
    }
  }
)

export const createCashGameTableThunk = createAsyncThunk(
  'create/cash-table',
  async ({ data, onSuccess }, thunkApi) => {
    try {
      const res = await createCashGameTable(data)
      if (onSuccess) onSuccess()
      return unwrapPayload(res)
    } catch (error) {
      return thunkApi.rejectWithValue(error.message)
    }
  }
)

export const updateCashGameTableThunk = createAsyncThunk(
  'update/cash-table',
  async ({ data, onSuccess }, thunkApi) => {
    try {
      const res = await updateCashGameTable(data)
      if (onSuccess) onSuccess()
      return unwrapPayload(res)
    } catch (error) {
      return thunkApi.rejectWithValue(error.message)
    }
  }
)

export const deleteCashGameTableThunk = createAsyncThunk(
  'delete/cash-table',
  async ({ tableId, onSuccess }, thunkApi) => {
    try {
      const res = await deleteCashGameTable(tableId)
      if (onSuccess) onSuccess()
      return unwrapPayload(res)
    } catch (error) {
      return thunkApi.rejectWithValue(error.message)
    }
  }
)

export const getCashGameHandsThunk = createAsyncThunk(
  'get/cash-game-hands',
  async (data, thunkApi) => {
    try {
      const res = await getCashGameHands(data)
      return unwrapPayload(res)
    } catch (error) {
      return thunkApi.rejectWithValue(error.message)
    }
  }
)

export const getCashGameHandHistoryThunk = createAsyncThunk(
  'get/cash-game-hand-history',
  async (data, thunkApi) => {
    try {
      const res = await getCashGameHandHistory(data)
      return unwrapPayload(res)
    } catch (error) {
      return thunkApi.rejectWithValue(error.message)
    }
  }
)

export const resetCashGameTableThunk = createAsyncThunk(
  'reset/cash-table',
  async ({ data, onSuccess }, thunkApi) => {
    try {
      const res = await resetCashGameTable(data)
      if (onSuccess) onSuccess()
      return unwrapPayload(res)
    } catch (error) {
      return thunkApi.rejectWithValue(error.message)
    }
  }
)

// Boot the FSM for every active cash-game table in the game engine.
export const initializeCashGameTablesThunk = createAsyncThunk(
  'initialize/cash-tables',
  async ({ onSuccess } = {}, thunkApi) => {
    try {
      const res = await initializeCashGameTables()
      if (onSuccess) onSuccess()
      return unwrapPayload(res)
    } catch (error) {
      return thunkApi.rejectWithValue(error.message)
    }
  }
)

export const getRakeConfigurationListThunk = createAsyncThunk(
  'gameTable/getRakeConfigurations',
  async (data, thunkApi) => {
    try {
      const response = await getRakeConfigurationList(data)
      return unwrapPayload(response)
    } catch (error) {
      if (!error[0]) throw error
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const getBuyInConfigurationListThunk = createAsyncThunk(
  'gameTable/getBuyInConfigurations',
  async (data, thunkApi) => {
    try {
      const response = await getBuyInConfigurationList(data)
      return unwrapPayload(response)
    } catch (error) {
      if (!error[0]) throw error
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const createBuyInConfigurationThunk = createAsyncThunk(
  'gameTable/createBuyInConfiguration',
  async ({ onSuccess, data }, thunkApi) => {
    try {
      const response = await createBuyInConfiguration(data)
      if (onSuccess) onSuccess()
      return unwrapPayload(response)
    } catch (error) {
      if (!error[0]) throw error
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const createRakeConfigurationThunk = createAsyncThunk(
  'gameTable/createRakeConfiguration',
  async ({ onSuccess, ...data }, thunkApi) => {
    try {
      const response = await createRakeConfiguration(data)
      if (onSuccess) onSuccess()
      return unwrapPayload(response)
    } catch (error) {
      if (!error[0]) throw error
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const deleteRakeConfigurationThunk = createAsyncThunk(
  'gameTable/deleteRakeConfiguration',
  async ({ onSuccess, ...data }, thunkApi) => {
    try {
      const response = await deleteRakeConfiguration(data)
      if (onSuccess) onSuccess()
      return unwrapPayload(response)
    } catch (error) {
      if (!error[0]) throw error
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const cloneRakeConfigurationThunk = createAsyncThunk(
  'gameTable/cloneRakeConfiguration',
  async ({ onSuccess, ...data }, thunkApi) => {
    try {
      const response = await cloneRakeConfiguration(data)
      if (onSuccess) onSuccess()
      return unwrapPayload(response)
    } catch (error) {
      if (!error[0]) throw error
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const fetchActivityDashboardThunk = createAsyncThunk(
  'get/activity-dashboard',
  async (params, thunkApi) => {
    try {
      const analytics = await getTablePerformanceAnalytics(params).catch(() => null)
      return { betsData: null, rakeData: analytics, roundsData: null }
    } catch (error) {
      return thunkApi.rejectWithValue(error?.[0]?.description || 'Failed to fetch activity dashboard')
    }
  }
)

export const fetchTablePerformanceAnalytics = createAsyncThunk(
  'gameTable/fetchTablePerformanceAnalytics',
  async (data, thunkApi) => {
    try {
      const response = await getTablePerformanceAnalytics(data)
      return response
    } catch (error) {
      if (!error[0]) throw error
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)